import React from 'react'
import { useStructuraAuth } from './useStructuraAuth'
import { Navigate } from 'react-router-dom'
import { useTranslation } from '@structura/i18n'

const PendingActivation: React.FC = () => {
  const authState = useStructuraAuth()
  const { t } = useTranslation()

  // If not authenticated, redirect to login
  if (!authState.isAuthenticated) {
    return <Navigate to="/account" replace />
  }

  // If already active, send the user back to the canonical landing — `/`
  // renders the dashboard inline when authenticated.
  if (authState.isActive) {
    return <Navigate to="/" replace />
  }

  return (
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-lg-8 col-xl-6">
          <div className="card border-0 shadow-lg" style={{ borderRadius: '1rem' }}>
            <div className="card-body p-5 text-center">
              {/* Welcome Icon */}
              <div className="mb-4">
                <div 
                  className="d-inline-flex align-items-center justify-content-center rounded-circle"
                  style={{ 
                    width: '80px', 
                    height: '80px', 
                    background: 'linear-gradient(135deg, forestgreen, seagreen)',
                    boxShadow: '0 4px 20px rgba(34, 139, 34, 0.4)'
                  }}
                >
                  <i className="bi bi-person-check text-white" style={{ fontSize: '2.5rem' }}></i>
                </div>
              </div>

              {/* Welcome Message */}
              <h2 className="fw-bold mb-3" style={{ color: 'forestgreen' }}>
                {t('auth.pending.welcome')}
              </h2>

              <p className="text-muted mb-4" style={{ fontSize: '1.1rem' }}>
                {t('auth.pending.thanksBefore')} <strong>{authState.userName || authState.userEmail}</strong>{t('auth.pending.thanksAfter')}
              </p>

              {/* Status Card */}
              <div 
                className="p-4 mb-4 rounded-3"
                style={{ 
                  background: 'linear-gradient(135deg, #f8f9fa, #e9ecef)',
                  border: '1px solid #dee2e6'
                }}
              >
                <div className="d-flex align-items-center justify-content-center mb-3">
                  <div className="spinner-grow spinner-grow-sm text-warning me-2" role="status">
                    <span className="visually-hidden">{t('auth.pending.processing')}</span>
                  </div>
                  <span className="badge bg-warning text-dark px-3 py-2" style={{ fontSize: '0.9rem' }}>
                    {t('auth.pending.statusBadge')}
                  </span>
                </div>

                <p className="mb-0 text-secondary">
                  {t('auth.pending.reviewNote')}
                </p>
              </div>

              {/* What's Coming */}
              <div className="text-start mb-4">
                <h5 className="fw-semibold mb-3">
                  <i className="bi bi-gift text-success me-2"></i>
                  {t('auth.pending.whatsWaiting')}
                </h5>
                <ul className="list-unstyled">
                  <li className="d-flex align-items-start mb-2">
                    <i className="bi bi-check-circle-fill text-success me-2 mt-1"></i>
                    <span><strong>{t('auth.pending.benefit1Label')}</strong>{t('auth.pending.benefit1Rest')}</span>
                  </li>
                  <li className="d-flex align-items-start mb-2">
                    <i className="bi bi-check-circle-fill text-success me-2 mt-1"></i>
                    <span><strong>{t('auth.pending.benefit2Label')}</strong>{t('auth.pending.benefit2Rest')}</span>
                  </li>
                  <li className="d-flex align-items-start mb-2">
                    <i className="bi bi-check-circle-fill text-success me-2 mt-1"></i>
                    <span><strong>{t('auth.pending.benefit3Label')}</strong>{t('auth.pending.benefit3Rest')}</span>
                  </li>
                  <li className="d-flex align-items-start mb-2">
                    <i className="bi bi-check-circle-fill text-success me-2 mt-1"></i>
                    <span><strong>{t('auth.pending.benefit4Label')}</strong>{t('auth.pending.benefit4Rest')}</span>
                  </li>
                  <li className="d-flex align-items-start">
                    <i className="bi bi-check-circle-fill text-success me-2 mt-1"></i>
                    <span><strong>{t('auth.pending.benefit5Label')}</strong>{t('auth.pending.benefit5Rest')}</span>
                  </li>
                </ul>
              </div>

              {/* Timeline Note */}
              <div className="alert border-0 mb-4" role="alert" style={{ background: 'rgba(74, 124, 89, 0.1)', color: '#3b6748' }}>
                <i className="bi bi-clock-history me-2"></i>
                <strong>{t('auth.pending.activationTimeLabel')}</strong>{t('auth.pending.activationTimeRest')}
              </div>

              {/* Contact Info */}
              <p className="text-muted small mb-0">
                {t('auth.pending.questions')}{' '}
                <a href="mailto:support@structuramatrix.com" className="text-decoration-none" style={{ color: 'forestgreen' }}>
                  support@structuramatrix.com
                </a>
              </p>
            </div>
          </div>

          {/* Sign Out Option */}
          <div className="text-center mt-4">
            <button 
              className="btn btn-outline-secondary btn-sm"
              onClick={() => authState.logout()}
            >
              <i className="bi bi-box-arrow-left me-1"></i>
              {t('auth.nav.signOut')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PendingActivation

